import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Plus } from "lucide-react";
import { toast } from "sonner";

const Complaints = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "general" });
  const [respondOpen, setRespondOpen] = useState(false);
  const [respond, setRespond] = useState({ id: "", status: "in_progress", response: "" });

  const canRespond = ["super_admin", "director", "receptionist"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/complaints"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/complaints", form);
      setList((l) => [data, ...l]); setOpen(false); setForm({ subject: "", description: "", category: "general" });
      toast.success("Complaint submitted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openRespond = (c) => { setRespond({ id: c.id, status: c.status || "in_progress", response: c.response || "" }); setRespondOpen(true); };

  const saveRespond = async () => {
    try {
      const { data } = await api.patch(`/complaints/${respond.id}`, { status: respond.status, response: respond.response });
      setList((l) => l.map((x) => x.id === data.id ? data : x));
      setRespondOpen(false); toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="complaints-page">
      <PageHeader
        overline="Engagement"
        title="Complaints"
        description="Parents and staff can raise concerns. The admin team responds and closes them out."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="new-complaint-btn"><Plus className="w-4 h-4 mr-1.5" /> New Complaint</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Raise a Complaint</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="complaint-subject" /></Fld>
                <Fld label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} data-testid="complaint-desc" /></Fld>
                <Fld label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="academic">Academic</SelectItem>
                      <SelectItem value="discipline">Discipline</SelectItem>
                      <SelectItem value="facility">Facility</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </Fld>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="complaint-submit">Submit</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {list.length === 0 ? <EmptyState message="No complaints filed yet." /> : (
        <div className="grid gap-3">
          {list.map(c => (
            <Card key={c.id} className="p-5 border-[#E2E8F0]" data-testid={`complaint-${c.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center">
                  <MessageSquareWarning className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-lg text-[#1B2C5A]">{c.subject}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E2E8F0]">{c.category}</span>
                    <span className={"text-xs px-2 py-0.5 rounded-full border " + (
                      c.status === "open" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      c.status === "in_progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-emerald-50 text-emerald-700 border-emerald-200")}>{c.status}</span>
                  </div>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.description}</p>
                  <div className="text-xs text-[#475569] mt-3">By {c.raised_by_name} ({c.raised_by_role}) · {new Date(c.created_at).toLocaleString()}</div>
                  {c.response && (
                    <div className="mt-3 p-3 bg-[#FDFBF7] border border-[#E2E8F0] rounded-md text-sm">
                      <div className="overline text-[#9DCD3F] mb-1">Response by {c.responded_by_name}</div>
                      <div className="text-[#1B2C5A]">{c.response}</div>
                    </div>
                  )}
                </div>
                {canRespond && (
                  <Button size="sm" variant="outline" onClick={() => openRespond(c)} data-testid={`complaint-respond-${c.id}`}>Respond</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Respond to Complaint</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <Fld label="Status">
              <Select value={respond.status} onValueChange={(v) => setRespond({ ...respond, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Response"><Textarea rows={4} value={respond.response} onChange={(e) => setRespond({ ...respond, response: e.target.value })} data-testid="respond-text" /></Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondOpen(false)}>Cancel</Button>
            <Button onClick={saveRespond} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="respond-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Complaints;
